export const PERMISSION_CATEGORIES = {
  ACTIONS: 1,
  INTEGRATIONS: 2,
  PROCESSES: 3,
  SYSTEM: 4,
  SMTP: 5,
  USERS: 6,
  DEPARTMENTS: 7,
  PAYMENTS: 8,
  CONVERSATIONS: 9,
  AI: 10,
  SQL_BUILDER: 11,
  DOCUMENT_SYSTEM: 12,
  OPERATIONS: 13,
  FREQUENCIES: 14,
  DASHBOARD: 15
} as const;

export type PermissionCategoryId =
  (typeof PERMISSION_CATEGORIES)[keyof typeof PERMISSION_CATEGORIES];

export type PermissionCode =
  | 'ACTION_VIEW'
  | 'ACTION_CREATE'
  | 'ACTION_UPDATE'
  | 'ACTION_DELETE'
  | 'INTEGRATION_VIEW'
  | 'INTEGRATION_CREATE'
  | 'INTEGRATION_UPDATE'
  | 'INTEGRATION_DELETE'
  | 'PROCESS_VIEW'
  | 'PROCESS_CREATE'
  | 'PROCESS_UPDATE'
  | 'PROCESS_DELETE'
  | 'SYSTEM_SETTING_CREATE'
  | 'SYSTEM_SETTING_UPDATE'
  | 'SYSTEM_SETTING_DELETE'
  | 'SMTP_VIEW'
  | 'SMTP_CREATE'
  | 'SMTP_UPDATE'
  | 'SMTP_DELETE'
  | 'SQL_BUILDER_FULL_ACCESS'
  | 'USER_ADMIN_READ'
  | 'USER_ADMIN_CREATE'
  | 'USER_ADMIN_UPDATE'
  | 'USER_ADMIN_DELETE'
  | 'ADMIN_USER_ACTION_ASSIGNMENT'
  | 'ROLE_VIEW'
  | 'ROLE_CREATE'
  | 'ROLE_UPDATE'
  | 'ROLE_DELETE'
  | 'ROLE_ASSIGN'
  | 'DEPARTMENT_VIEW'
  | 'DEPARTMENT_CREATE'
  | 'DEPARTMENT_UPDATE'
  | 'DEPARTMENT_DELETE'
  | 'PAYMENT_PACKAGE_CREATE'
  | 'PAYMENT_PACKAGE_UPDATE'
  | 'CONVERSATION_FEEDBACK_READ'
  | 'AI_HISTORY_READ_ANY'
  | 'DOCUMENT_VIEW'
  | 'DOCUMENT_UPLOAD'
  | 'OPERATION_VIEW'
  | 'OPERATION_CREATE'
  | 'OPERATION_UPDATE'
  | 'OPERATION_DELETE'
  | 'FREQUENCY_VIEW'
  | 'FREQUENCY_CREATE'
  | 'FREQUENCY_UPDATE'
  | 'FREQUENCY_DELETE'
  | 'DASHBOARD_VIEW'
  | 'DASHBOARD_CREATE'
  | 'DASHBOARD_UPDATE'
  | 'DASHBOARD_DELETE';

export interface PermissionDefinition {
  code: PermissionCode;
  description: string;
  descriptionTr?: string;
  nameTr?: string;
  isVisible?: boolean;
  dependsOn?: PermissionCode[];
}

export const PERMISSIONS_BY_CATEGORY: Record<
  PermissionCategoryId,
  readonly PermissionDefinition[]
> = {
  [PERMISSION_CATEGORIES.ACTIONS]: [
    { code: 'ACTION_VIEW', description: 'View actions and their details', nameTr: 'Görüntüleme', descriptionTr: 'Aksiyonları ve detaylarını görüntüle' },
    { code: 'ACTION_CREATE', description: 'Create new actions', nameTr: 'Ekleme', descriptionTr: 'Yeni aksiyon oluştur' },
    { code: 'ACTION_UPDATE', description: 'Modify existing actions', nameTr: 'Düzenleme', descriptionTr: 'Mevcut aksiyonları düzenle' },
    { code: 'ACTION_DELETE', description: 'Delete or archive actions', nameTr: 'Silme', descriptionTr: 'Aksiyonları sil veya arşivle' }
  ],
  [PERMISSION_CATEGORIES.DOCUMENT_SYSTEM]: [
    { code: 'DOCUMENT_VIEW', description: 'View documents', nameTr: 'Görüntüleme', descriptionTr: 'Dokümanları görüntüle' },
    { code: 'DOCUMENT_UPLOAD', description: 'Upload documents', nameTr: 'Yükleme', descriptionTr: 'Doküman yükle' }
  ],
  [PERMISSION_CATEGORIES.OPERATIONS]: [
    { code: 'OPERATION_VIEW', description: 'View operations', nameTr: 'Görüntüleme', descriptionTr: 'Operasyonları görüntüle', isVisible: true, dependsOn: ['ACTION_VIEW'] },
    { code: 'OPERATION_CREATE', description: 'Create operations', nameTr: 'Ekleme', descriptionTr: 'Operasyon oluştur', isVisible: false },
    { code: 'OPERATION_UPDATE', description: 'Update operations', nameTr: 'Düzenleme', descriptionTr: 'Operasyonları güncelle', isVisible: false },
    { code: 'OPERATION_DELETE', description: 'Delete operations', nameTr: 'Silme', descriptionTr: 'Operasyonları sil', isVisible: false }
  ],
  [PERMISSION_CATEGORIES.FREQUENCIES]: [
    { code: 'FREQUENCY_VIEW', description: 'View frequencies', nameTr: 'Görüntüleme', descriptionTr: 'Sıklıkları görüntüle', isVisible: true, dependsOn: ['ACTION_VIEW'] },
    { code: 'FREQUENCY_CREATE', description: 'Create frequencies', nameTr: 'Ekleme', descriptionTr: 'Sıklık oluştur', isVisible: false },
    { code: 'FREQUENCY_UPDATE', description: 'Update frequencies', nameTr: 'Düzenleme', descriptionTr: 'Sıklıkları güncelle', isVisible: false },
    { code: 'FREQUENCY_DELETE', description: 'Delete frequencies', nameTr: 'Silme', descriptionTr: 'Sıklıkları sil', isVisible: false }
  ],
  [PERMISSION_CATEGORIES.INTEGRATIONS]: [
    { code: 'INTEGRATION_VIEW', description: 'Read integration metadata', nameTr: 'Görüntüleme', descriptionTr: 'Entegrasyon bilgilerini oku' },
    { code: 'INTEGRATION_CREATE', description: 'Create integrations', nameTr: 'Ekleme', descriptionTr: 'Entegrasyon oluştur' },
    { code: 'INTEGRATION_UPDATE', description: 'Update integrations and related resources', nameTr: 'Düzenleme', descriptionTr: 'Entegrasyonları ve ilgili kaynakları güncelle' },
    { code: 'INTEGRATION_DELETE', description: 'Delete integrations or related resources', nameTr: 'Silme', descriptionTr: 'Entegrasyonları veya ilgili kaynakları sil' }
  ],
  [PERMISSION_CATEGORIES.PROCESSES]: [
    { code: 'PROCESS_VIEW', description: 'View processes', nameTr: 'Görüntüleme', descriptionTr: 'Süreçleri görüntüle', isVisible: true, dependsOn: ['ACTION_VIEW'] },
    { code: 'PROCESS_CREATE', description: 'Create processes or sub-processes', nameTr: 'Ekleme', descriptionTr: 'Süreç veya alt süreç oluştur' },
    { code: 'PROCESS_UPDATE', description: 'Update processes or sub-processes', nameTr: 'Düzenleme', descriptionTr: 'Süreç veya alt süreçleri güncelle' },
    { code: 'PROCESS_DELETE', description: 'Delete processes or sub-processes', nameTr: 'Silme', descriptionTr: 'Süreç veya alt süreçleri sil' }
  ],
  [PERMISSION_CATEGORIES.SYSTEM]: [
    { code: 'SYSTEM_SETTING_CREATE', description: 'Create system settings', nameTr: 'Ekleme', descriptionTr: 'Sistem ayarları oluştur' , isVisible: false},
    { code: 'SYSTEM_SETTING_UPDATE', description: 'Update system settings', nameTr: 'Düzenleme', descriptionTr: 'Sistem ayarlarını güncelle' , isVisible: false},
    { code: 'SYSTEM_SETTING_DELETE', description: 'Delete system settings', nameTr: 'Silme', descriptionTr: 'Sistem ayarlarını sil', isVisible: false }
  ],
  [PERMISSION_CATEGORIES.SMTP]: [
    { code: 'SMTP_VIEW', description: 'View SMTP settings', nameTr: 'Görüntüleme', descriptionTr: 'SMTP ayarlarını görüntüle' },
    { code: 'SMTP_CREATE', description: 'Create SMTP settings', nameTr: 'Ekleme', descriptionTr: 'SMTP ayarları oluştur' },
    { code: 'SMTP_UPDATE', description: 'Update SMTP settings', nameTr: 'Düzenleme', descriptionTr: 'SMTP ayarlarını güncelle' },
    { code: 'SMTP_DELETE', description: 'Delete SMTP settings', nameTr: 'Silme', descriptionTr: 'SMTP ayarlarını sil' }
  ],
  [PERMISSION_CATEGORIES.USERS]: [
    { code: 'USER_ADMIN_READ', description: 'View admin user information', nameTr: 'Görüntüleme', descriptionTr: 'Yönetici kullanıcı bilgilerini görüntüle', isVisible: true },
    { code: 'USER_ADMIN_CREATE', description: 'Create new users', nameTr: 'Ekleme', descriptionTr: 'Yeni kullanıcı oluştur', isVisible: true },
    { code: 'USER_ADMIN_UPDATE', description: 'Update user details or status', nameTr: 'Düzenleme', descriptionTr: 'Kullanıcı bilgilerini veya durumunu güncelle', isVisible: true },
    { code: 'USER_ADMIN_DELETE', description: 'Deactivate or delete users', nameTr: 'Silme', descriptionTr: 'Kullanıcıları devre dışı bırak veya sil', isVisible: true },
    { code: 'ROLE_VIEW', description: 'View roles', nameTr: 'Rol Görüntüleme', descriptionTr: 'Rolleri görüntüle', dependsOn: ['USER_ADMIN_READ'] },
    { code: 'ROLE_CREATE', description: 'Create roles', nameTr: 'Rol Ekleme', descriptionTr: 'Rol oluştur', dependsOn: ['USER_ADMIN_READ'] },
    { code: 'ROLE_UPDATE', description: 'Update roles', nameTr: 'Rol Düzenleme', descriptionTr: 'Rolleri güncelle', dependsOn: ['USER_ADMIN_READ'] },
    { code: 'ROLE_DELETE', description: 'Delete roles', nameTr: 'Rol Silme', descriptionTr: 'Rolleri sil', dependsOn: ['USER_ADMIN_READ'] },
    { code: 'ROLE_ASSIGN', description: 'Assign roles to users', nameTr: 'Rol Atama', descriptionTr: 'Kullanıcılara rol ata' },
    { code: 'ADMIN_USER_ACTION_ASSIGNMENT', description: 'Assign roles to users', nameTr: 'Rapor Yetkisi Verme', descriptionTr: 'Kullanıcılar için rapor yetkisi verme' }
  ],
  [PERMISSION_CATEGORIES.SQL_BUILDER]: [
    { code: 'SQL_BUILDER_FULL_ACCESS', description: 'Full access to SQL builder', nameTr: 'Tam Erişim', descriptionTr: 'SQL builder\'a tam erişim' }
  ],
  [PERMISSION_CATEGORIES.DEPARTMENTS]: [
    { code: 'DEPARTMENT_VIEW', description: 'View departments', nameTr: 'Görüntüleme', descriptionTr: 'Departmanları görüntüle', dependsOn: ['USER_ADMIN_READ'] },
    { code: 'DEPARTMENT_CREATE', description: 'Create departments', nameTr: 'Ekleme', descriptionTr: 'Departman oluştur', dependsOn: ['USER_ADMIN_READ'] },
    { code: 'DEPARTMENT_UPDATE', description: 'Update departments', nameTr: 'Düzenleme', descriptionTr: 'Departmanları güncelle', dependsOn: ['USER_ADMIN_READ'] },
    { code: 'DEPARTMENT_DELETE', description: 'Delete departments', nameTr: 'Silme', descriptionTr: 'Departmanları sil', dependsOn: ['USER_ADMIN_READ'] }
  ],
  [PERMISSION_CATEGORIES.PAYMENTS]: [
    { code: 'PAYMENT_PACKAGE_CREATE', description: 'Create payment packages', nameTr: 'Ekleme', descriptionTr: 'Ödeme paketi oluştur', isVisible: false },
    { code: 'PAYMENT_PACKAGE_UPDATE', description: 'Update payment packages', nameTr: 'Düzenleme', descriptionTr: 'Ödeme paketlerini güncelle', isVisible: false }
  ],
  [PERMISSION_CATEGORIES.CONVERSATIONS]: [
    { code: 'CONVERSATION_FEEDBACK_READ', description: 'Read conversation feedback', nameTr: 'Okuma', descriptionTr: 'Sohbet geri bildirimlerini oku', dependsOn: ['USER_ADMIN_READ'] }
  ],
  [PERMISSION_CATEGORIES.AI]: [
    { code: 'AI_HISTORY_READ_ANY', description: 'Access AI history for any user', nameTr: 'Geçmişe Erişim', descriptionTr: 'Herhangi bir kullanıcının AI geçmişine eriş', dependsOn: ['USER_ADMIN_READ'] }
  ],
  [PERMISSION_CATEGORIES.DASHBOARD]: [
    { code: 'DASHBOARD_VIEW', description: 'View dashboard', nameTr: 'Görüntüleme', descriptionTr: 'Dashboard\'ı görüntüle' },
    { code: 'DASHBOARD_CREATE', description: 'Create dashboard', nameTr: 'Ekleme', descriptionTr: 'Dashboard oluştur' },
    { code: 'DASHBOARD_UPDATE', description: 'Update dashboard', nameTr: 'Düzenleme', descriptionTr: 'Dashboard\'ı güncelle' },
    { code: 'DASHBOARD_DELETE', description: 'Delete dashboard', nameTr: 'Silme', descriptionTr: 'Dashboard\'ı sil' }
  ]
};

export const STATIC_PERMISSION_CODES: PermissionCode[] = Object.values(PERMISSIONS_BY_CATEGORY)
  .flat()
  .map(permission => permission.code);

export const SYSTEM_ROLE_CODES = {
  ADMIN: 'admin',
  EDITOR: 'editor',
  USER: 'user'
} as const;

export type SystemRoleCode = (typeof SYSTEM_ROLE_CODES)[keyof typeof SYSTEM_ROLE_CODES];

export const ROLE_PERMISSION_SEED: Record<SystemRoleCode, PermissionCode[]> = {
  [SYSTEM_ROLE_CODES.USER]: ['ACTION_VIEW', 'DOCUMENT_VIEW', 'DASHBOARD_VIEW'],
  [SYSTEM_ROLE_CODES.EDITOR]: [
    'ACTION_VIEW',
    'ACTION_CREATE',
    'ACTION_UPDATE',
    'ACTION_DELETE',
    'INTEGRATION_VIEW',
    'INTEGRATION_CREATE',
    'INTEGRATION_UPDATE',
    'INTEGRATION_DELETE',
    'PROCESS_VIEW',
    'PROCESS_CREATE',
    'PROCESS_UPDATE',
    'PROCESS_DELETE',
    'SYSTEM_SETTING_CREATE',
    'SYSTEM_SETTING_UPDATE',
    'SYSTEM_SETTING_DELETE',
    'SMTP_VIEW',
    'SQL_BUILDER_FULL_ACCESS',
    'DOCUMENT_VIEW',
    'DOCUMENT_UPLOAD',
    'OPERATION_VIEW',
    'OPERATION_CREATE',
    'OPERATION_UPDATE',
    'OPERATION_DELETE',
    'FREQUENCY_VIEW',
    'FREQUENCY_CREATE',
    'FREQUENCY_UPDATE',
    'FREQUENCY_DELETE',
    'DASHBOARD_VIEW',
    'DASHBOARD_CREATE',
    'DASHBOARD_UPDATE',
    'DASHBOARD_DELETE'
  ],
  [SYSTEM_ROLE_CODES.ADMIN]: [
    'ACTION_VIEW',
    'ACTION_CREATE',
    'ACTION_UPDATE',
    'ACTION_DELETE',
    'INTEGRATION_VIEW',
    'INTEGRATION_CREATE',
    'INTEGRATION_UPDATE',
    'INTEGRATION_DELETE',
    'PROCESS_VIEW',
    'PROCESS_CREATE',
    'PROCESS_UPDATE',
    'PROCESS_DELETE',
    'SYSTEM_SETTING_CREATE',
    'SYSTEM_SETTING_UPDATE',
    'SYSTEM_SETTING_DELETE',
    'SMTP_VIEW',
    'SMTP_CREATE',
    'SMTP_UPDATE',
    'SMTP_DELETE',
    'USER_ADMIN_READ',
    'USER_ADMIN_CREATE',
    'USER_ADMIN_UPDATE',
    'USER_ADMIN_DELETE',
    'ADMIN_USER_ACTION_ASSIGNMENT',
    'ROLE_VIEW',
    'ROLE_CREATE',
    'ROLE_UPDATE',
    'ROLE_DELETE',
    'ROLE_ASSIGN',
    'DEPARTMENT_VIEW',
    'DEPARTMENT_CREATE',
    'DEPARTMENT_UPDATE',
    'DEPARTMENT_DELETE',
    'PAYMENT_PACKAGE_CREATE',
    'PAYMENT_PACKAGE_UPDATE',
    'CONVERSATION_FEEDBACK_READ',
    'AI_HISTORY_READ_ANY',
    'SQL_BUILDER_FULL_ACCESS',
    'DOCUMENT_VIEW',
    'DOCUMENT_UPLOAD',
    'OPERATION_VIEW',
    'OPERATION_CREATE',
    'OPERATION_UPDATE',
    'OPERATION_DELETE',
    'FREQUENCY_VIEW',
    'FREQUENCY_CREATE',
    'FREQUENCY_UPDATE',
    'FREQUENCY_DELETE',
    'DASHBOARD_VIEW',
    'DASHBOARD_CREATE',
    'DASHBOARD_UPDATE',
    'DASHBOARD_DELETE'
  ]
};

export function validatePermissionCode(value: string): value is PermissionCode {
  return (STATIC_PERMISSION_CODES as string[]).includes(value);
}

export const SYSTEM_ROLES: readonly SystemRoleCode[] = Object.values(SYSTEM_ROLE_CODES);
